import React from 'react';
import { toast } from 'sonner';
import { ClipboardList, Edit, Plus, Search, Trash2 } from 'lucide-react';
import { confirmDialog } from '../ui/confirm-dialog';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

export default function FocusProductsSubmenu({
  axiosInstance,
  focusProducts,
  setFocusProducts,
  focusProductFilter,
  setFocusProductFilter,
  isAddProductModalOpen,
  setIsAddProductModalOpen,
  isEditProductModalOpen,
  setIsEditProductModalOpen,
  editingProduct,
  setEditingProduct,
  newFocusProduct,
  setNewFocusProduct,
}) {
  return (
    <>
      <div className="flex flex-col space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Focus Products</h2>
            <p className="text-gray-600 dark:text-gray-200">Focus products monitoring</p>
          </div>
        </div>

        <div className="mb-4 flex items-center justify-between">
          <Button onClick={() => setIsAddProductModalOpen(true)} className="bg-indigo-600 text-white hover:bg-indigo-700">
            <Plus className="mr-2 h-4 w-4" />
            Add Product
          </Button>

          <div className="relative w-1/3">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500 dark:text-gray-300" />
            <Input
              placeholder="Search Brand, Model, or Category..."
              className="pl-8"
              value={focusProductFilter}
              onChange={(e) => setFocusProductFilter(e.target.value)}
            />
          </div>

          <div className="w-[100px]" />
        </div>

        <Card className="glass border-0 shadow-lg">
          <CardContent className="py-6">
            {focusProducts.length === 0 ? (
              <div className="py-12 text-center">
                <ClipboardList className="mx-auto mb-4 h-16 w-16 text-gray-400" />
                <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">No Focus Products</h3>
                <p className="text-gray-500 dark:text-gray-300">Click "Add Product" to add items to the list.</p>
              </div>
            ) : (
              <div className="flex justify-center overflow-x-auto">
                <Table className="mx-auto w-full max-w-4xl">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center">Brand</TableHead>
                      <TableHead className="text-center">Model</TableHead>
                      <TableHead className="text-center">Category</TableHead>
                      <TableHead className="w-[120px] text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {focusProducts
                      .filter((product) =>
                        product.brand.toLowerCase().includes(focusProductFilter.toLowerCase()) ||
                        product.model.toLowerCase().includes(focusProductFilter.toLowerCase()) ||
                        product.category.toLowerCase().includes(focusProductFilter.toLowerCase())
                      )
                      .map((product, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-center font-medium">{product.brand}</TableCell>
                          <TableCell className="text-center">{product.model}</TableCell>
                          <TableCell className="text-center">{product.category}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setEditingProduct(product);
                                  setIsEditProductModalOpen(true);
                                }}
                                className="h-8 w-8 text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-black"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={async () => {
                                  const confirmed = await confirmDialog({
                                    title: 'Delete Product',
                                    description: 'Are you sure you want to delete this product?',
                                    variant: 'destructive',
                                  });

                                  if (!confirmed) return;

                                  try {
                                    await axiosInstance.delete(`/focus-products/${product.id}`);
                                    setFocusProducts(focusProducts.filter((item) => item.id !== product.id));
                                    toast.success('Product deleted successfully');
                                  } catch (error) {
                                    console.error('Error deleting product:', error);
                                    toast.error('Failed to delete product');
                                  }
                                }}
                                className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-black"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={isAddProductModalOpen} onOpenChange={setIsAddProductModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Focus Product</DialogTitle>
            <DialogDescription>Enter the details of the new product to track.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="brand" className="text-right">Brand</Label>
              <Input
                id="brand"
                value={newFocusProduct.brand}
                onChange={(e) => setNewFocusProduct({ ...newFocusProduct, brand: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="model" className="text-right">Model</Label>
              <Input
                id="model"
                value={newFocusProduct.model}
                onChange={(e) => setNewFocusProduct({ ...newFocusProduct, model: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="category" className="text-right">Category</Label>
              <Input
                id="category"
                value={newFocusProduct.category}
                onChange={(e) => setNewFocusProduct({ ...newFocusProduct, category: e.target.value })}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={async () => {
                if (!newFocusProduct.brand || !newFocusProduct.model || !newFocusProduct.category) {
                  toast.error('Please fill in all fields');
                  return;
                }

                try {
                  const response = await axiosInstance.post('/focus-products', newFocusProduct);
                  setFocusProducts([...focusProducts, response.data]);
                  setNewFocusProduct({ brand: '', model: '', category: '' });
                  setIsAddProductModalOpen(false);
                  toast.success('Product added successfully');
                } catch (error) {
                  console.error('Error adding focus product:', error);
                  toast.error('Failed to add focus product');
                }
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditProductModalOpen} onOpenChange={setIsEditProductModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Focus Product</DialogTitle>
            <DialogDescription>Update the details of the focus product.</DialogDescription>
          </DialogHeader>
          {editingProduct && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-brand" className="text-right">Brand</Label>
                <Input
                  id="edit-brand"
                  value={editingProduct.brand}
                  onChange={(e) => setEditingProduct({ ...editingProduct, brand: e.target.value })}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-model" className="text-right">Model</Label>
                <Input
                  id="edit-model"
                  value={editingProduct.model}
                  onChange={(e) => setEditingProduct({ ...editingProduct, model: e.target.value })}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-category" className="text-right">Category</Label>
                <Input
                  id="edit-category"
                  value={editingProduct.category}
                  onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })}
                  className="col-span-3"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={async () => {
                if (!editingProduct || !editingProduct.brand || !editingProduct.model || !editingProduct.category) {
                  toast.error('Please fill in all fields');
                  return;
                }

                try {
                  const response = await axiosInstance.put(`/focus-products/${editingProduct.id}`, editingProduct);
                  setFocusProducts(focusProducts.map((product) => (product.id === editingProduct.id ? response.data : product)));
                  setIsEditProductModalOpen(false);
                  setEditingProduct(null);
                  toast.success('Product updated successfully');
                } catch (error) {
                  console.error('Error updating focus product:', error);
                  toast.error('Failed to update focus product');
                }
              }}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
